import React from 'react';
import PropTypes from 'prop-types';
import { ProfileSettingsConfig } from '@Constants/rx-user/constants';
import { RadioButtonGroup } from '@els/els-react--radio-button';
import { Slider } from '@els/els-react--slider';
import { SingleSelect } from '@els/els-react--select';
import ProfileSettingsAccordionItem from '@Pages/ProfileSettings/Components/ProfileSettingsAccordionItem';
import PreferencesTooltip from '@Pages/ProfileSettings/Preferences/components/PreferencesTooltip';
import { Flyout } from '@els/els-react--flyout';

import './SynthesisPlannerPublished.scss';

function SynthesisPlannerPublished({ preferences, options, onChange, onSave, onClose, onReset }) {
  const fullRoutesValues =
    options.RX2_AUTOPLAN_FULL_ROUTES && options.RX2_AUTOPLAN_FULL_ROUTES.values
      ? options.RX2_AUTOPLAN_FULL_ROUTES.values.slice().reverse()
      : [];

  const renderValue = () => [
    {
      title: ProfileSettingsConfig.Labels.numberOfPlansToCreate,
      value: preferences.RX2_AUTOPLAN_FULL_ROUTES
        ? `${ProfileSettingsConfig.Labels.numberOfPlansToCreate} = ${preferences.RX2_AUTOPLAN_MAX_BEST_PATHS}`
        : `${ProfileSettingsConfig.Labels.lastStepOnly}`,
    },
    {
      title: ProfileSettingsConfig.Labels.maxAlternativeBranches,
      value: `${ProfileSettingsConfig.Labels.maxAlternativeBranches} = ${preferences.RX2_AUTOPLAN_MAX_ALTERNATIVE_BRANCHES}`,
    },
    {
      title: ProfileSettingsConfig.Labels.maxNumberOfSteps,
      value: `${ProfileSettingsConfig.Labels.maxNumberOfSteps} = ${preferences.RX2_AUTOPLAN_MAX_DEPTH}`,
    },
    {
      title: ProfileSettingsConfig.Labels.stopSearchingIfStartingMaterialIsCommerciallyAvailable,
      value:
        preferences.RX2_AUTOPLAN_STOP_WHEN_COMMERCIALLY_AVAILABLE &&
        ProfileSettingsConfig.Labels.stopSearchingIfStartingMaterialIsCommerciallyAvailable,
    },
    {
      title: ProfileSettingsConfig.Labels.defaultYieldForReactionsWithoutGivenYield,
      value: `${ProfileSettingsConfig.Labels.defaultYieldForReactionsWithoutGivenYield} = ${preferences.RX2_AUTOPLAN_DEFAULT_YIELD}%`,
    },
  ];

  return (
    <ProfileSettingsAccordionItem
      onClose={onClose}
      onSave={onSave}
      label={ProfileSettingsConfig.Labels.publishedDefaultSettings}
      dataE2E="profile-settings-accordion-row-published-default-settings"
      value={renderValue()}
      resetButton
      titled
      onReset={onReset}
      validateOnSave
      className="synthesis-planner-published-accordion-item preferences-accordion-item">
      <div className="preferences-accordion-item__wrapper">
        <PreferencesTooltip
          name="length-and-depth-of-synthesis-plans"
          label="Length & depth of synthesis plans"
          content={[
            <span key="full-route-description">
              The option &quot;Full route&quot; in published retrosynthesis will create routes to the target molecule by searching the
              Reaxys reaction content from published literature. You have the ability to select the maximum No of routes to be generated
              between 1 to 10 by using the drop down. The default is 5. The length of the various routes is determined by the stop
              criterions selected below.
            </span>,
            <span key="last-step-only-description">
              The option &quot;Last step only&quot; will find and display up to 100 preparations from Reaxys reaction content. When looking
              at them in tree views you can further expand the routes by adding additional steps.
            </span>,
          ]}
        />
        <div className="synthesis-planner-dropdown-row">
          <div className="synthesis-planner-published-accordion-item__routes-options">
            <fieldset className="synthesis-planner-published__fieldset">
              <legend className="sr-only">Length &amp; depth of synthesis plans</legend>
              <ul className="rx-radio_button-group preferences-accordion-item__selectOption_label">
                {fullRoutesValues.map((option) => (
                  <li key={`full-routes-${option.value}`}>
                    <RadioButtonGroup.Option
                      id={`full-routes-${option.value}`}
                      name="full-routes"
                      label={option.label}
                      value={option.value}
                      checked={preferences.RX2_AUTOPLAN_FULL_ROUTES === option.value}
                      onChange={() => onChange('RX2_AUTOPLAN_FULL_ROUTES', option.value)}
                    />
                  </li>
                ))}
              </ul>
            </fieldset>
          </div>
          {preferences.RX2_AUTOPLAN_FULL_ROUTES && (
            <SingleSelect
              className="preferences-accordion-item__selectOption_singleSelect--margin"
              label={ProfileSettingsConfig.Labels.numberOfPlansToCreate}
              shouldHideLabel
              data-e2e="synthesis-planner-number-of-plans-to-create-select"
              selectedOptions={[preferences.RX2_AUTOPLAN_MAX_BEST_PATHS]}
              options={options.RX2_AUTOPLAN_MAX_BEST_PATHS?.values}
              onSelect={({ value }) => {
                onChange('RX2_AUTOPLAN_MAX_BEST_PATHS', value);
              }}
              title="numberOfPlansToCreate"
              size="small"
              optionsFlyoutProps={{
                noInternalPadding: true,
                theme: 'default',
                placement: 'bottom',
                pointer: true,
                pointerType: 'primary',
              }}
              required
              hideRequired
            />
          )}
        </div>
        <hr className="preferences-accordion-item__separator" />
        <div className="synthesis-planner-dropdown-row">
          <div>{ProfileSettingsConfig.Labels.maxAlternativeBranches}</div>
          <SingleSelect
            className="preferences-accordion-item__selectOption"
            label={ProfileSettingsConfig.Labels.maxAlternativeBranches}
            shouldHideLabel
            selectedOptions={[preferences.RX2_AUTOPLAN_MAX_ALTERNATIVE_BRANCHES]}
            options={options.RX2_AUTOPLAN_MAX_ALTERNATIVE_BRANCHES.values}
            onSelect={({ value }) => onChange('RX2_AUTOPLAN_MAX_ALTERNATIVE_BRANCHES', value)}
            title="maxAlternativeBranches"
            data-e2e="synthesis-planner-max-alternative-branches-select"
            size="small"
            optionsFlyoutProps={{ noInternalPadding: true, theme: 'default', placement: 'bottom', pointer: true, pointerType: 'primary' }}
            required
            hideRequired
          />
        </div>
        <div className="synthesis-planner-dropdown-row">
          <div>{ProfileSettingsConfig.Labels.maxNumberOfSteps}</div>
          <SingleSelect
            className="preferences-accordion-item__selectOption"
            label={ProfileSettingsConfig.Labels.maxNumberOfSteps}
            shouldHideLabel
            data-e2e="synthesis-planner-max-number-of-steps-select"
            selectedOptions={[preferences.RX2_AUTOPLAN_MAX_DEPTH]}
            options={options.RX2_AUTOPLAN_MAX_DEPTH.values}
            onSelect={({ value }) => onChange('RX2_AUTOPLAN_MAX_DEPTH', value)}
            title="maxNumberOfSteps"
            size="small"
            optionsFlyoutProps={{ noInternalPadding: true, theme: 'default', placement: 'bottom', pointer: true, pointerType: 'primary' }}
            required
            hideRequired
          />
        </div>

        <div className="preferences-accordion-item__option">
          <div className="preferences-accordion-item__option_label">
            {ProfileSettingsConfig.Labels.stopSearchingIfStartingMaterialIsCommerciallyAvailable}
          </div>
          <fieldset className="synthesis-planner-published__fieldset">
            <legend className="sr-only">{ProfileSettingsConfig.Labels.stopSearchingIfStartingMaterialIsCommerciallyAvailable}</legend>
            <ul className="rx-radio_button-group">
              {options.RX2_AUTOPLAN_STOP_WHEN_COMMERCIALLY_AVAILABLE.values.map((commercialOption) => (
                <li key={`option-${commercialOption.value}`} className="u-els-margin-bottom-1o2">
                  <RadioButtonGroup.Option
                    id={`radio-${commercialOption.value}`}
                    name="radio"
                    label={commercialOption.label}
                    value={commercialOption.value}
                    checked={preferences.RX2_AUTOPLAN_STOP_WHEN_COMMERCIALLY_AVAILABLE === commercialOption.value}
                    onChange={() => onChange('RX2_AUTOPLAN_STOP_WHEN_COMMERCIALLY_AVAILABLE', commercialOption.value)}
                  />
                </li>
              ))}
            </ul>
          </fieldset>
        </div>
        <hr className="preferences-accordion-item__separator" />
        <div className="preferences-accordion-item__option">
          <div className="preferences-accordion-item__option_label">
            {ProfileSettingsConfig.Labels.defaultYieldForReactionsWithoutGivenYield}
          </div>
          <div className="synthesis-planner-slider__container">
            <Slider
              reactSliderProps={{
                className: 'preferences-accordion-item__slider slider--primary',
                min: Number(options.RX2_AUTOPLAN_DEFAULT_YIELD.values[0].value),
                max: Number(options.RX2_AUTOPLAN_DEFAULT_YIELD.values[options.RX2_AUTOPLAN_DEFAULT_YIELD.values.length - 1].value),
                step:
                  Number(options.RX2_AUTOPLAN_DEFAULT_YIELD.values[1].value) - Number(options.RX2_AUTOPLAN_DEFAULT_YIELD.values[0].value) ||
                  10,
                defaultValue: Number(preferences.RX2_AUTOPLAN_DEFAULT_YIELD),
                onChange: (val) => onChange('RX2_AUTOPLAN_DEFAULT_YIELD', val),
                renderThumb: (props, state) => {
                  const extraClassName = props.className ? ` ${props.className}` : '';
                  const thumbClassName = `slider_currentValue${extraClassName}`;

                  return (
                    <div
                      {...props}
                      aria-label={ProfileSettingsConfig.Labels.defaultYieldForReactionsWithoutGivenYield}
                      aria-valuetext={`${state.valueNow}%`}>
                      <Flyout flyout={state.valueNow} trigger="hover" placement="top-start" pointerType="primary">
                        <div aria-hidden="true" className={thumbClassName} />
                      </Flyout>
                    </div>
                  );
                },
              }}
            />
          </div>
        </div>
      </div>
    </ProfileSettingsAccordionItem>
  );
}

SynthesisPlannerPublished.propTypes = {
  preferences: PropTypes.object,
  options: PropTypes.object,
  onChange: PropTypes.func,
  onSave: PropTypes.func,
  onClose: PropTypes.func,
  onReset: PropTypes.func,
};

export default SynthesisPlannerPublished;
